import React from "react";
import { irrigatorViewModel } from "common/viewModels";
import SummaryRow from "./SummaryRow";
import MonthlySummary from "./MonthlySummary";
import * as domain from "common/domain";
import Alert from "components/Alert";
import * as utils from "common/utils";
import Grid from "components/Grid";
import GridCell from "components/GridCell";
import { Doughnut, Bar } from "react-chartjs-2";
import FileSummary from "./FileSummary";
import Watermark from "components/Watermark";

const groupLabel = (group) => {
    switch (group) {
        case 1:
            return "Fixed depth and return period; visual assessment/dig a hole";
        case 2:
            return "Trigger point; fixed depth applied";
        case 3:
            return "Depth applied to achieve target; fixed return period";
        case 4:
            return "Trigger point and depth applied to achieve target";
        default:
            return "Application depth";
    }
};

const Irrigation = ({ before, after, refData, showFileComparison = true }) => {
    const budget = after.details;
    const vm = irrigatorViewModel(budget, refData);
    const baseVm = before && irrigatorViewModel(before.details, refData);

    if (vm.length === 0 && (!baseVm || baseVm.length === 0))
        return (
            <div className="u-pt-lg">
                <Alert text="The selected analysis does not contain irrigation information" type="Info" />
            </div>
        );

    const scenarios = [after];
    if (before) scenarios.unshift(before);

    const getData = (viewModel, group) => {
        return domain.farmYear.map((month) => {
            let monthTotal = 0;
            for (const irrSys of viewModel) {
                for (const app of irrSys.applications.filter((a) => a.group === group && a.months.find((m) => m.month === month && m.reportingYear))) {
                    monthTotal += app.area;
                }
            }
            return utils.round(monthTotal, 1);
        });
    };

    const getVmGroups = (name, viewModel, before) => {
        return [1, 2, 3, 4, 5].map((grp) => ({
            stack: name,
            label: groupLabel(grp),
            fill: false,
            lineTension: 0.1,
            backgroundColor: before ? "#E3EEF8" : "#c6f3c6",
            borderColor: before ? "#509BC2" : "#52af6e",
            borderWidth: 1,
            data: getData(viewModel, grp),
        }));
    };

    const datasets = baseVm ? [...getVmGroups(before.displayName, baseVm, true), ...getVmGroups(after.displayName, vm, false)] : getVmGroups(after.displayName, vm, false);

    const monthlyAreaData = {
        datasets,
        labels: domain.farmYear,
    };

    const barOptions = {
        scales: {
            y: {
                display: true,
                title: { display: true, text: "Area (ha)" },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
        },
        maintainAspectRatio: false,
        responsive: true,
    };

    return (
        <div className="ReportPage">
            {showFileComparison && before && <FileSummary before={before} after={after} mini={true} />}
            <IrrigationSummary before={before} after={after} refData={refData} showExtra={true} vm={vm} baseVm={baseVm} />
            <div className="ReportSection">
                <h1>Irrigated Amounts by Month</h1>
                <MonthlySummary scenarios={scenarios} categoryName="Irrigation" rowName="Applied" />
            </div>
            <div className="ReportSection">
                <h1>Irrigated Area by Month</h1>
                <div className="printable-chart-container">
                    <Bar data={monthlyAreaData} options={barOptions} className="printable-chart" />
                </div>
            </div>
        </div>
    );
};

export default Irrigation;

const getTotalData = (viewModel, group) => {
    let monthTotal = 0;
    for (const irrSys of viewModel) {
        monthTotal += irrSys.applications.filter((a) => a.group === group && a.months.find((m) => m.reportingYear)).length;
    }
    return monthTotal;
};

export const IrrigationSummary = ({ after, before, refData, showExtra, vm, baseVm, heading }) => {
    vm = vm || irrigatorViewModel(after.details, refData);
    baseVm = baseVm || (before && irrigatorViewModel(before.details, refData));

    if (vm.length === 0 && (!baseVm || baseVm.length === 0)) return null;

    const CalcValues = ({ srcKey, render, group }) => {
        switch (srcKey) {
            case "group": {
                const actualApplications = vm.reduce((t, i) => (t += i.applications.reduce((mt, m) => (mt += m.months.filter((m) => m.reportingYear).length), 0)), 0);
                const baseApplications = baseVm && baseVm.reduce((t, i) => (t += i.applications.reduce((mt, m) => (mt += m.months.filter((m) => m.reportingYear).length), 0)), 0);
                const actualGroupApplications = vm.reduce((t, i) => (t += i.applications.filter((a) => a.group === group).reduce((mt, m) => (mt += m.months.filter((m) => m.reportingYear).length), 0)), 0);
                const baseGroupApplications = baseVm && baseVm.reduce((t, i) => (t += i.applications.filter((a) => a.group === group).reduce((mt, m) => (mt += m.months.filter((m) => m.reportingYear).length), 0)), 0);
                const actualGroupValue = (actualGroupApplications / (actualApplications > 0 ? actualApplications : 1)) * 100;
                const baseGroupValue = (baseGroupApplications / (baseApplications > 0 ? baseApplications : 1)) * 100;
                return render(utils.round(baseGroupValue, 0), utils.round(actualGroupValue, 0));
            }
            default: {
                const actualValue = vm.filter((s) => s.irrigatorType === srcKey || srcKey === "area").reduce((t, i) => (t += i.area), 0);
                const baseValue = baseVm && baseVm.filter((s) => s.irrigatorType === srcKey || srcKey === "area").reduce((t, i) => (t += i.area), 0);
                return render(baseValue, actualValue);
            }
        }
    };

    const irrigatorTypes = [];

    vm.forEach((i) => {
        if (!irrigatorTypes.includes(i.irrigatorType)) {
            irrigatorTypes.push(i.irrigatorType);
        }
    });

    baseVm &&
        baseVm.forEach((i) => {
            if (!irrigatorTypes.includes(i.irrigatorType)) {
                irrigatorTypes.push(i.irrigatorType);
            }
        });

    const groups = [1, 2, 3, 4, 5];

    const assessmentData = (group, dataset) => {
        const irrVm = irrigatorViewModel(dataset.details, refData);
        //const total = irrVm.reduce((t,i)=> t+= i.area,0)
        const data = getTotalData(irrVm, group);
        //return total > 0 ? utils.round(data / total * 100,2) : null
        return utils.round(data, 2);
    };

    const afterDs = groups.map((n) => assessmentData(n, after));
    const afterLabels = groups.map((n) => groupLabel(n)).filter((l, i) => afterDs[i] > 0);
    const beforeDs = before && groups.map((n) => assessmentData(n, before));
    const beforeLabels = before && groups.map((n) => groupLabel(n)).filter((l, i) => beforeDs[i] > 0);

    const beforeData = before && {
        labels: beforeLabels,
        datasets: [
            {
                data: beforeDs.filter((n) => n > 0),
                backgroundColor: groups.map((g) => utils.graphColours[g]),
                hoverBackgroundColor: groups.map((g) => utils.graphColours[g]),
            },
        ],
    };

    const afterData = {
        labels: afterLabels,
        datasets: [
            {
                data: afterDs.filter((n) => n > 0),
                backgroundColor: groups.map((g) => utils.graphColours[g]),
                hoverBackgroundColor: groups.map((g) => utils.graphColours[g]),
            },
        ],
    };

    const options = {
        plugins: {
            legend: {
                display: true,
            },
        },
        responsive: false,
        maintainAspectRatio: false,
    };

    return (
        <>
            <div className="ReportSection">
                <h1>Irrigation</h1>
                <div className="Table Table-Compressed">
                    <Watermark />
                    <table>
                        <thead>
                            <tr>
                                <th></th>
                                {baseVm && (
                                    <th className="u-before u-textCenter" data-width="12.5">
                                        {utils.truncateText(before.displayName, 35)}
                                    </th>
                                )}
                                <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                    {utils.truncateText(after.displayName, 35)}
                                </th>
                                {baseVm && (
                                    <th className="u-textCenter" data-width="16.7">
                                        Change
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            <CalcValues srcKey="area" render={(baseValue, actualValue) => <SummaryRow baseValue={baseValue} actualValue={actualValue} label="Total irrigated area" unit="ha" />} />
                            {irrigatorTypes.map((t, i) => (
                                <CalcValues srcKey={t} key={i} render={(baseValue, actualValue) => <SummaryRow baseValue={baseVm ? baseValue || 0 : undefined} actualValue={actualValue} label={t} unit="ha" />} />
                            ))}
                        </tbody>
                    </table>
                </div>
                {false && <Alert type="info" text={<div>The following table groups the application area by common soil moisture assessment types.</div>} />}
                {false && (
                    <Grid>
                        {before && (
                            <GridCell className="u-lg-width1of2 u-md-width1of1">
                                <div className="h4 u-textCenter">{before.displayName}</div>
                                <div className="u-flex u-flexJustifyCenter">
                                    <Doughnut data={beforeData} options={options} height={400} width={400} />
                                </div>
                            </GridCell>
                        )}
                        <GridCell className="u-lg-width1of2 u-md-width1of1">
                            <div className="h4 u-textCenter">{after.displayName}</div>
                            <div className="u-flex u-flexJustifyCenter">
                                <Doughnut data={afterData} options={options} height={400} width={400} />
                            </div>
                        </GridCell>
                    </Grid>
                )}
            </div>
            <div className="ReportSection">
                <div className="Table Table-Compressed">
                    <Watermark />
                    <table>
                        <thead>
                            <tr>
                                <th>SOIL MOISTURE ASSESSMENT TYPE USAGE</th>

                                {baseVm && (
                                    <th className="u-before u-textCenter" data-width="12.5">
                                        {utils.truncateText(before.displayName, 35)}
                                    </th>
                                )}
                                <th className={before ? "u-after u-textCenter" : "u-textCenter"} data-width="12.5">
                                    {utils.truncateText(after.displayName, 35)}
                                </th>
                                {baseVm && (
                                    <th className="u-textCenter" data-width="16.7">
                                        Change
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            <CalcValues srcKey="group" group={1} render={(baseValue, actualValue) => <SummaryRow baseValue={baseVm && baseValue} actualValue={actualValue} label={groupLabel(1)} unit="%" />} />
                            <CalcValues srcKey="group" group={2} render={(baseValue, actualValue) => <SummaryRow baseValue={baseVm && baseValue} actualValue={actualValue} label={groupLabel(2)} unit="%" />} />
                            <CalcValues srcKey="group" group={3} render={(baseValue, actualValue) => <SummaryRow baseValue={baseVm && baseValue} actualValue={actualValue} label={groupLabel(3)} unit="%" />} />
                            <CalcValues srcKey="group" group={4} render={(baseValue, actualValue) => <SummaryRow baseValue={baseVm && baseValue} actualValue={actualValue} label={groupLabel(4)} unit="%" />} />
                            <CalcValues srcKey="group" group={5} render={(baseValue, actualValue) => <SummaryRow baseValue={baseVm && baseValue} actualValue={actualValue} label={groupLabel(5)} unit="%" />} />
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};
